import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { v4 as uuidv4 } from 'uuid';
import barangays from './barangays.json';
import { useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import Preloader from './Preloader'; 

const libraries = ['places', 'marker'];

function PendingShelters() {
  const [shelters, setShelters] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending'); // Default to pending
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    house_name: '',
    house_addr: '',
    barangay: '',
    contact_no: '',
    members: 0,
    capacity: 0,
    house_image1: '', 
    house_image2: '',
    house_image3: '',
    house_image4: '',
    provider_name: '',
    status: 'pending',
    longitude: null,
    latitude: null
  });
  const [imageFiles, setImageFiles] = useState([null, null, null, null]);
  const autocompleteRef = useRef(null);
  const navigate = useNavigate();
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA", // Replace with your actual Google Maps API Key
    libraries
  });

  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(shelters.length / itemsPerPage);
  const paginatedShelters = shelters.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  useEffect(() => {
      document.title = "Pending Temporary Shelters";
    }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();

    // Setup real-time subscription for temp_shelter
    const tempShelterChannel = supabase
      .channel('public:temp_shelter')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'temp_shelter' },
        (payload) => {
          console.log('Temporary shelter change received!', payload);
          // Re-fetch data on any change (INSERT, UPDATE, DELETE)
          fetchPendingShelters();
        }
      )
      .subscribe();

    return () => {
      // Clean up the subscription when the component unmounts
      supabase.removeChannel(tempShelterChannel);
    };
  }, []); // Empty dependency array to run only once on mount

  useEffect(() => {
    if (currentUser) {
      fetchPendingShelters();
    }
  }, [currentUser, search, statusFilter]);

  async function fetchPendingShelters() {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      let query = supabase
        .from('temp_shelter')
        .select('*');

      if (search) {
        // Search by house name OR address, case-insensitive
        query = query.or(`house_name.ilike.%${search}%,house_addr.ilike.%${search}%`);
      }

      // Filter for pending and rejected statuses only (remove approved)
      query = query.in('status', ['pending', 'rejected']);

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching shelters:', error);
        setShelters([]);
      } else {
        setShelters(data || []);
      }
    } catch (error) {
      console.error('Error in fetchPendingShelters:', error);
      setShelters([]);
    } finally {
      setLoading(false);
    }
  }

  function handlePlaceSelect() {
    if (autocompleteRef.current && autocompleteRef.current.getPlace()) {
      const place = autocompleteRef.current.getPlace();
      const address = place.formatted_address;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      setForm(prev => ({
        ...prev,
        house_addr: address,
        latitude: lat,
        longitude: lng
      }));
    }
  }

  function handleUseCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setForm(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
          }));

          // Reverse geocode to get address for display
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              setForm(prev => ({ ...prev, house_addr: results[0].formatted_address }));
            } else {
              console.error('Geocoder failed due to:', status);
              alert('Could not determine address from current location.');
            }
          });
        },
        (error) => {
          console.error('Error getting current location:', error);
          alert('Unable to retrieve your current location. Please ensure location services are enabled and granted permission.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  }

  async function handleImageUpload(event, index) {
    const file = event.target.files[0];
    if (file) {
      try {
        const newImageFiles = [...imageFiles];
        newImageFiles[index] = file;
        setImageFiles(newImageFiles);

        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(7);
        const fileExt = file.name.split('.').pop();
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('shelter-images')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          alert('Failed to upload image. Please try again.');
          return;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('shelter-images')
          .getPublicUrl(filePath);

        setForm(prev => ({
          ...prev,
          [`house_image${index + 1}`]: publicUrl
        }));

      } catch (error) {
        console.error('Error in handleImageUpload:', error);
        alert('Failed to upload image. Please try again.');
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    if (form.latitude === null || form.longitude === null) {
      alert('Please provide a location by searching or using your current location.');
      setLoading(false);
      return;
    }

    try {
      const shelterData = {
        id: uuidv4(),
        house_name: form.house_name,
        house_addr: form.house_addr,
        barangay: form.barangay,
        contact_no: form.contact_no,
        members: parseInt(form.members) || 0,
        capacity: parseInt(form.capacity) || 0,
        house_image1: form.house_image1 || null,
        house_image2: form.house_image2 || null,
        house_image3: form.house_image3 || null,
        house_image4: form.house_image4 || null,
        provider_name: form.provider_name,
        status: 'pending',
        longitude: form.longitude,
        latitude: form.latitude
      };

      let actionType = 'added temporary shelter';
      let targetId = shelterData.id;
      if (showForm && form.id) {
        // Editing existing shelter
        actionType = 'edited temporary shelter';
        targetId = form.id;
        const { error } = await supabase
          .from('temp_shelter')
          .update(shelterData)
          .eq('id', form.id);
        if (error) throw error;
      } else {
        // Adding new shelter
        const { error } = await supabase
          .from('temp_shelter')
          .insert([shelterData]);
        if (error) throw error;
      }
      // Audit log
      if (currentUser) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: actionType,
          target_id: targetId,
          target_type: 'TemporaryShelter',
          details: shelterData
        }]);
      }
      alert('Temporary shelter saved successfully!');
      setShowForm(false);
      setForm({
        house_name: '',
        house_addr: '',
        barangay: '',
        contact_no: '',
        members: 0,
        capacity: 0,
        house_image1: '',
        house_image2: '',
        house_image3: '',
        house_image4: '',
        provider_name: '',
        status: 'pending',
        longitude: null,
        latitude: null
      });
      setImageFiles([null, null, null, null]);
      fetchPendingShelters();
    } catch (error) {
      alert(`Failed to save shelter: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(shelterId) {
    try {
      const { error } = await supabase
        .from('temp_shelter')
        .update({ status: 'approved' })
        .eq('id', shelterId);
      if (error) throw error;
      // Audit log for accept
      if (currentUser) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'accepted temporary shelter',
          target_id: shelterId,
          target_type: 'TemporaryShelter',
          details: { status: 'approved' }
        }]);
      }
      alert('Shelter approved successfully!');
      fetchPendingShelters();
    } catch (error) {
      alert(`Failed to approve shelter: ${error.message}`);
    }
  }

  async function handleReject(shelterId) {
    try {
      const { error } = await supabase
        .from('temp_shelter')
        .update({ status: 'rejected' })
        .eq('id', shelterId);
      if (error) throw error;
      // Audit log for reject
      if (currentUser) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'rejected temporary shelter',
          target_id: shelterId,
          target_type: 'TemporaryShelter',
          details: { status: 'rejected' }
        }]);
      }
      alert('Shelter rejected successfully!');
      fetchPendingShelters();
    } catch (error) {
      alert(`Failed to reject shelter: ${error.message}`);
    }
  }

  if (!currentUser || !isLoaded) {
    return <Preloader text="Loading Shelters..." />;;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">Pending Temporary Shelters</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        {currentUser?.role === 'barangay' && (
          <div style={{
            background: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffeeba',
            borderRadius: 8,
            padding: '16px 24px',
            marginBottom: 24,
            fontWeight: 500,
            fontSize: 16
          }}>
            Barangay users have view-only access to pending shelters. Please contact an admin for changes.
          </div>
        )}
        {/* Pending Shelters Section */}
        <div>
          <div className="search-actions-row">
            <div className="search-filters">
              <input
                type="text"
                placeholder="Search by house name or address"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
              />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="type-filter"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            {currentUser?.role !== 'barangay' && (
              <div className="action-buttons">
                <button 
                  onClick={() => setShowForm(true)}
                  className="create-button"
                >
                  <FaPlus /> Add New Shelter
                </button>
              </div>
            )}
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>Image</th>
                  <th style={{ padding: 12 }}>Name</th>
                  <th style={{ padding: 12 }}>Address</th>
                  <th style={{ padding: 12 }}>Barangay</th>
                  <th style={{ padding: 12 }}>Contact No.</th>
                  <th style={{ padding: 12 }}>Members</th>
                  <th style={{ padding: 12 }}>Shelter Capacity</th>
                  <th style={{ padding: 12 }}>Provider</th>
                  <th style={{ padding: 12 }}>Status</th>
                  <th style={{ padding: 12 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedShelters.length > 0 ? (
                  paginatedShelters.map(shelter => (
                    <tr 
                      key={shelter.id} 
                      style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                      onClick={() => navigate(`/pending-shelter-detail/${shelter.id}`)}
                    >
                      <td style={{ padding: 12 }}>
                        <img 
                          src={shelter.house_image1 || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo='} 
                          alt={shelter.house_name}
                          style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
                        />
                      </td>
                      <td style={{ padding: 12 }}>{shelter.house_name}</td>
                      <td style={{ padding: 12 }}>{shelter.house_addr}</td>
                      <td style={{ padding: 12 }}>{shelter.barangay || '-'}</td>
                      <td style={{ padding: 12 }}>{shelter.contact_no}</td>
                      <td style={{ padding: 12 }}>{shelter.members}</td>
                      <td style={{ padding: 12 }}>{shelter.capacity}</td>
                      <td style={{ padding: 12 }}>{shelter.provider_name}</td>
                      <td style={{ padding: 12 }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 14,
                          background: shelter.status === 'pending' ? '#fff3cd' : shelter.status === 'rejected' ? '#fce8e6' : '#f0f0f0',
                          color: shelter.status === 'pending' ? '#856404' : shelter.status === 'rejected' ? '#d32f2f' : '#888',
                          border: '1px solid #eee',
                          minWidth: 70,
                          textAlign: 'center'
                        }}>{shelter.status.charAt(0).toUpperCase() + shelter.status.slice(1)}</span>
                      </td>
                      <td style={{ padding: 12 }}>
                        {currentUser?.role !== 'barangay' && shelter.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove(shelter.id);
                              }}
                              style={{ 
                                background: '#19c37d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '8px 16px',
                                cursor: 'pointer'
                              }}
                            >
                              Approve
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReject(shelter.id);
                              }}
                              style={{ 
                                background: '#dc3545',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '8px 16px',
                                cursor: 'pointer'
                              }}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        {currentUser?.role !== 'barangay' && shelter.status === 'rejected' && (
                          <div style={{ color: '#d32f2f', fontSize: 14, fontStyle: 'italic' }}>Rejected</div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No pending shelters found.</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>Previous</button>
              <div style={{ display: 'flex', gap: 4 }}>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setPage(i + 1)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      background: page === i + 1 ? '#08b1c7' : '#fff',
                      color: page === i + 1 ? '#fff' : '#333',
                      fontWeight: page === i + 1 ? 'bold' : 'normal'
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
            </div>
          </div>
        </div>
        {/* Add/Edit Form Modal */}
        {showForm && isLoaded && currentUser?.role !== 'barangay' && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 24,
                width: '90%',
                maxWidth: 600,
                height: 700,
                boxShadow: '0 4px 32px rgba(8, 177, 199, 0.18)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 24,
                  flexShrink: 0
                }}
              >
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>Add New Shelter</h2>
                <button
                  onClick={() => setShowForm(false)}
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 28,
                    cursor: 'pointer',
                    color: '#888'
                  }}
                >
                  ×
                </button>
              </div>
              <form
                onSubmit={handleSubmit}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Household Name</label>
                  <input
                    type="text"
                    value={form.house_name}
                    onChange={e => setForm(prev => ({ ...prev, house_name: e.target.value }))}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Location</label>
                  <Autocomplete
                    onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                    onPlaceChanged={handlePlaceSelect}
                  >
                    <input
                      type="text"
                      placeholder="Search for address"
                      value={form.house_addr}
                      onChange={e => setForm(prev => ({ ...prev, house_addr: e.target.value }))}
                      required
                      style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                    />
                  </Autocomplete>
                  <button
                    type="button"
                    onClick={handleUseCurrentLocation}
                    style={{
                      marginTop: 8,
                      border: '1px solid #19c37d',
                      background: '#e6fff4',
                      color: '#19c37d',
                      padding: '10px 20px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Use Current Location
                  </button>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Barangay</label>
                  <select
                    value={form.barangay}
                    onChange={e => setForm(prev => ({ ...prev, barangay: e.target.value }))}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  >
                    <option value="">Select Barangay</option>
                    {barangays.map(barangay => (
                      <option key={barangay.number} value={barangay.name}>{barangay.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Contact Number</label>
                  <input
                    type="tel"
                    value={form.contact_no}
                    onChange={e => setForm(prev => ({ ...prev, contact_no: e.target.value }))}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Members <span style={{ fontSize: '0.8em', color: '#666' }}>(current members of the household currently residing in the shelter)</span></label>
                  <input
                    type="number"
                    value={form.members}
                    onChange={e => setForm(prev => ({ ...prev, members: e.target.value }))}
                    required
                    min="0"
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Total Shelter Capacity</label>
                  <input
                    type="number"
                    value={form.capacity}
                    onChange={e => setForm(prev => ({ ...prev, capacity: e.target.value }))}
                    required
                    min="0"
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Provider Name</label>
                  <input
                    type="text"
                    value={form.provider_name}
                    onChange={e => setForm(prev => ({ ...prev, provider_name: e.target.value }))}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Images (up to 4)</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                    {[0, 1, 2, 3].map((index) => (
                      <div key={index}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, index)}
                          style={{ width: '100%' }}
                        />
                        {form[`house_image${index + 1}`] && (
                          <img
                            src={form[`house_image${index + 1}`]}
                            alt={`Preview ${index + 1}`}
                            style={{ width: '100%', height: 150, objectFit: 'cover', marginTop: 8, borderRadius: 4 }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </form>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  marginTop: 24,
                  flexShrink: 0,
                  background: '#fff'
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    background: '#fff',
                    color: '#333',
                    fontSize: 16,
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="pending-shelter-form"
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: 'none',
                    background: loading ? '#ccc' : '#19c37d',
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => document.querySelector('form').requestSubmit()}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PendingShelters;